import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, View } from 'react-native';
import { Configuration } from '../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { component } from '../../Decorators/Component';
import { AnimatedTextView } from '../Typography/AnimatedTextView';
import { IconComponent } from '../Icons/IconComponent';
import { AnimatedPressable } from '../Buttons/AnimatedPressable';
import { CoreConfig } from '../../../CoreConfig';
import { useAnimation } from '../Animations/AnimationHooks';
const iconSize = 12;
export const ChoiceItemStepperView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.stepper,
    style(theme, mediaQuery, window, props) {
        var _a;
        return {
            container: {
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center'
            },
            title: {
                fontSize: 16,
                lineHeight: 20,
            },
            description: {},
            text: {
                fontSize: 16,
                marginVertical: 5,
                minWidth: 34,
                textAlign: 'center'
            },
            btn: {
                height: 24,
                width: 24,
                paddingHorizontal: 4,
                borderWidth: 2,
                borderRadius: 12,
                borderColor: ((_a = theme.colors) === null || _a === void 0 ? void 0 : _a.secondary_color) || '#115AC9'
            },
            btnDisabled: {
                borderColor: '#BFCCDA'
            }
        };
    }
})(({ style, value, id, testID, onChange, title, description, light, disabled, min_value, max_value, customize }) => {
    var _a, _b, _c, _d;
    const [hoverMinus, setHoverMinus] = useAnimation({ initialValue: 1, duration: 100 });
    const [hoverPlus, setHoverPlus] = useAnimation({ initialValue: 1, duration: 100 });
    const [disabledIncrease, setDisabledIncrease] = useState(false);
    const [disabledDecrease, setDisabledDecrease] = useState(false);
    useEffect(() => {
        if (typeof max_value === 'number') {
            setDisabledIncrease(max_value <= value);
        }
        if (typeof min_value === 'number') {
            setDisabledDecrease(min_value >= value);
        }
    }, [value]);
    const handleIncrease = useCallback(() => {
        const step = typeof (customize === null || customize === void 0 ? void 0 : customize.step) === 'number'
            ? customize.step
            : 1;
        if (typeof max_value === 'number' && max_value < value + step) {
            return;
        }
        !disabled && (onChange === null || onChange === void 0 ? void 0 : onChange(id, value + step));
    }, [value, disabled, customize === null || customize === void 0 ? void 0 : customize.step]);
    const handleDecrease = useCallback(() => {
        const step = typeof (customize === null || customize === void 0 ? void 0 : customize.step) === 'number'
            ? customize.step
            : 1;
        if (typeof min_value === 'number' && min_value > value - step) {
            return;
        }
        !disabled && (onChange === null || onChange === void 0 ? void 0 : onChange(id, value - step));
    }, [value, disabled, customize === null || customize === void 0 ? void 0 : customize.step]);
    const handleMouseOverMinus = useCallback(() => {
        !disabled && !disabledDecrease && setHoverMinus(0.7);
    }, [disabled, disabledDecrease]);
    const handleMouseLeaveMinus = useCallback(() => {
        !disabled && setHoverMinus(1);
    }, [disabled]);
    const handleMouseOverPlus = useCallback(() => {
        !disabled && !disabledIncrease && setHoverPlus(0.7);
    }, [disabled, disabledIncrease]);
    const handleMouseLeavePlus = useCallback(() => {
        !disabled && setHoverPlus(1);
    }, [disabled]);
    const iconStyle = useMemo(() => {
        return {
            width: iconSize,
            height: iconSize,
            marginTop: Platform.select({
                default: 4,
                web: 0.5
            })
        };
    }, []);
    const decreaseIconColor = disabled || disabledDecrease
        ? (_a = style === null || style === void 0 ? void 0 : style.btnDisabled) === null || _a === void 0 ? void 0 : _a.borderColor
        : (_b = style === null || style === void 0 ? void 0 : style.btn) === null || _b === void 0 ? void 0 : _b.borderColor;
    const increaseIconColor = disabled || disabledIncrease
        ? (_c = style === null || style === void 0 ? void 0 : style.btnDisabled) === null || _c === void 0 ? void 0 : _c.borderColor
        : (_d = style === null || style === void 0 ? void 0 : style.btn) === null || _d === void 0 ? void 0 : _d.borderColor;
    return (_jsxs(View, { style: style === null || style === void 0 ? void 0 : style.container, children: [_jsx(AnimatedPressable, { onMouseOver: handleMouseOverMinus,
                onMouseLeave: handleMouseLeaveMinus, style: [style === null || style === void 0 ? void 0 : style.btn, { opacity: hoverMinus }, (disabled || disabledDecrease) && (style === null || style === void 0 ? void 0 : style.btnDisabled)], onPress: handleDecrease, testID: `choice-item-${testID || title || ''}-minus`, children: _jsx(IconComponent, { name: 'si-minus', size: iconSize, color: decreaseIconColor, containerStyle: iconStyle }) }), _jsx(AnimatedTextView, { textStyle: style === null || style === void 0 ? void 0 : style.text, light: light, children: value }), _jsx(AnimatedPressable, { onMouseOver: handleMouseOverPlus,
                onMouseLeave: handleMouseLeavePlus, style: [style === null || style === void 0 ? void 0 : style.btn, { opacity: hoverPlus }, (disabled || disabledIncrease) && (style === null || style === void 0 ? void 0 : style.btnDisabled)], onPress: handleIncrease, testID: `choice-item-${testID || title || ''}-plus`, children: _jsx(IconComponent, { name: 'si-plus', size: iconSize, color: increaseIconColor, containerStyle: iconStyle }) })] }));
});
